import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, Textarea, useSave } from "@ncp/ui";
import type { StageChecklistItem } from "../review/api";

/**
 * The Department's refusal of a party's request it decides on – a complainant's request to withdraw (Condition 3.2; Regulations Article 22(1)) –
 * with reasons the complainant is told. Offered wherever the request's confirmation is: the stage's primary action and the checklist row. The
 * refusal sends no letter: the complainant reads the reasons in the portal's notice and the file's history, and the complaint continues.
 */
export function useRefuseRequest(caseId: string | undefined) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const [refusing, setRefusing] = useState<StageChecklistItem | null>(null);
  const [refusal, setRefusal] = useState("");
  const close = () => { setRefusing(null); setRefusal(""); };
  const refuse = useSave({
    mutationFn: (taskId: string) => api.post(`/api/cases/${caseId}/workflow/tasks/${taskId}/refuse`, { reason: refusal.trim() }),
    saved: t("backoffice:r3Process.left.refused"),
    onSuccess: () => { close(); for (const k of [["case", caseId], ["case-workflow", caseId], ["case-review", caseId], ["case-tasks", caseId], ["case-events", caseId]]) void qc.invalidateQueries({ queryKey: k }); },
  });
  const dialog = (
    <Dialog open={refusing !== null} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t("backoffice:r3Process.left.refuseTitle")}</DialogTitle>
          <DialogDescription dir="auto">{refusing ? (locale === "ar" ? refusing.titleAr : refusing.title) : null}</DialogDescription>
        </DialogHeader>
        <Field label={t("backoffice:r3Process.left.refuseReasons")} hint={t("backoffice:r3Process.left.refuseHint")}>
          {(id) => <Textarea id={id} rows={4} value={refusal} onChange={(e) => setRefusal(e.target.value)} draft={refusing ? `case:${caseId}:task:${refusing.key}:refusal` : null} />}
        </Field>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>{t("actions.cancel")}</Button>
          <Button variant="destructive" disabled={refusal.trim().length < 10} loading={refuse.isPending} onClick={() => refusing && refuse.mutate(refusing.key)}>{t("backoffice:r3Process.left.refuse")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { open: (item: StageChecklistItem) => setRefusing(item), pending: refuse.isPending, dialog };
}
